import Image from '@tiptap/extension-image';
import { ResizableNodeView } from '@tiptap/core';

/**
 * Image extension with corner resize, rotation, alignment, and caption.
 * Google Docs-style: drag corners to resize, drag top handle to rotate.
 */
export const ImageWithAlign = Image.extend({
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      height: { default: null },
      rotate: {
        default: 0,
        parseHTML: (el) => parseFloat(el.getAttribute('data-rotate')) || 0,
        renderHTML: (attrs) => (attrs.rotate != null && attrs.rotate !== 0 ? { 'data-rotate': String(attrs.rotate) } : {}),
      },
      align: {
        default: 'center',
        parseHTML: (el) => el.getAttribute('data-align') || 'center',
        renderHTML: (attrs) => (attrs.align ? { 'data-align': attrs.align } : {}),
      },
      caption: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-caption') || null,
        renderHTML: (attrs) => (attrs.caption ? { 'data-caption': attrs.caption } : {}),
      },
    };
  },

  addNodeView() {
    const resizeOpt = this.options.resize;
    if (!resizeOpt?.enabled || typeof document === 'undefined') return null;

    const {
      directions = ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      minWidth = 50,
      minHeight = 50,
      alwaysPreserveAspectRatio = true,
    } = resizeOpt;

    return ({ node, getPos, HTMLAttributes, editor }) => {
      const img = document.createElement('img');
      img.src = HTMLAttributes.src || '';
      img.alt = HTMLAttributes.alt || '';
      img.className = (this.options.HTMLAttributes?.class || '') + ' rounded-xl shadow-md';
      img.draggable = false;
      img.style.display = 'block';
      ['data-align', 'data-caption', 'data-rotate'].forEach((k) => {
        if (HTMLAttributes[k] != null) img.setAttribute(k, String(HTMLAttributes[k]));
      });

      const rotate = node.attrs.rotate ?? 0;
      let w = node.attrs.width;
      let h = node.attrs.height;

      const rotateWrapper = document.createElement('div');
      rotateWrapper.style.cssText = 'position:relative;display:inline-block;transform-origin:center center;overflow:visible;';
      rotateWrapper.appendChild(img);

      const contentEl = document.createElement('div');
      contentEl.style.cssText = 'position:relative;display:inline-block;overflow:visible;';
      contentEl.appendChild(rotateWrapper);

      const applySize = (width, height) => {
        if (width && height) {
          contentEl.style.width = `${width}px`;
          contentEl.style.height = `${height}px`;
          contentEl.style.maxWidth = '';
          rotateWrapper.style.width = `${width}px`;
          rotateWrapper.style.height = `${height}px`;
          img.style.width = `${width}px`;
          img.style.height = `${height}px`;
          img.style.maxWidth = '';
        } else {
          contentEl.style.width = '';
          contentEl.style.height = '';
          contentEl.style.maxWidth = '100%';
          rotateWrapper.style.width = '';
          rotateWrapper.style.height = '';
          img.style.width = '';
          img.style.height = '';
          img.style.maxWidth = '100%';
        }
      };

      const applyRotate = (deg) => {
        rotateWrapper.style.transform = deg ? `rotate(${deg}deg)` : '';
      };

      if (w && h) {
        applySize(w, h);
      }
      applyRotate(rotate);

      const nodeView = new ResizableNodeView({
        element: contentEl,
        editor,
        node,
        getPos,
        onResize: (width, height) => {
          applySize(width, height);
        },
        onCommit: (width, height) => {
          const pos = getPos();
          if (pos !== undefined) {
            editor.chain().setNodeSelection(pos).updateAttributes(this.name, { width, height }).run();
          }
        },
        onUpdate: (updatedNode) => updatedNode.type === node.type,
        options: {
          directions,
          min: { width: minWidth, height: minHeight },
          preserveAspectRatio: alwaysPreserveAspectRatio,
        },
      });

      const figure = document.createElement('figure');
      figure.className = 'dawaer-image-figure-editor';
      figure.style.textAlign = (node.attrs.align || 'center') === 'left' ? 'left' : (node.attrs.align || 'center') === 'right' ? 'right' : 'center';

      const wrapper = document.createElement('div');
      wrapper.className = 'dawaer-image-resize-rotate-wrapper';
      wrapper.style.cssText = 'position:relative;display:inline-block;';
      wrapper.appendChild(nodeView.dom);

      const rotationHandle = document.createElement('div');
      rotationHandle.className = 'dawaer-image-rotation-handle';
      rotationHandle.setAttribute('data-rotation-handle', '');
      rotationHandle.title = 'تدوير الصورة';

      let currentRotate = rotate;

      const getCenter = () => {
        const rect = contentEl.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      };

      const angleFromCenter = (cx, cy, px, py) => {
        return (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
      };

      const onRotateStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const ev = e.touches ? e.touches[0] : e;
        const center = getCenter();
        const startAngle = angleFromCenter(center.x, center.y, ev.clientX, ev.clientY);
        const startDeg = currentRotate;

        const onMove = (moveEv) => {
          const mev = moveEv.touches ? moveEv.touches[0] : moveEv;
          const c = getCenter();
          const currAngle = angleFromCenter(c.x, c.y, mev.clientX, mev.clientY);
          let delta = currAngle - startAngle;
          let newDeg = startDeg + delta;
          newDeg = Math.round(newDeg);
          if (newDeg >= 360) newDeg -= 360;
          if (newDeg < 0) newDeg += 360;
          currentRotate = newDeg;
          applyRotate(newDeg);
          const pos = getPos();
          if (pos !== undefined) {
            editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, null, { ...node.attrs, rotate: newDeg }));
          }
        };

        const onEnd = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onEnd);
          document.removeEventListener('touchmove', onMove, { passive: false });
          document.removeEventListener('touchend', onEnd);
          const pos = getPos();
          if (pos !== undefined) {
            editor.chain().setNodeSelection(pos).updateAttributes(this.name, { rotate: currentRotate }).run();
          }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onEnd);
      };

      rotationHandle.addEventListener('mousedown', onRotateStart);
      rotationHandle.addEventListener('touchstart', onRotateStart, { passive: false });

      wrapper.appendChild(rotationHandle);
      figure.appendChild(wrapper);

      const figcaption = document.createElement('figcaption');
      figcaption.className = 'dawaer-image-caption-editor text-gray-500 mt-2';
      figcaption.style.fontFamily = 'Amiri, serif';
      figcaption.textContent = node.attrs.caption || '';
      figure.appendChild(figcaption);

      img.onload = () => {
        if (!w || !h) {
          const nw = img.naturalWidth;
          const nh = img.naturalHeight;
          let maxWidth = editor.view.dom?.parentElement?.clientWidth ?? editor.view.dom?.offsetWidth ?? 740;
          if (!maxWidth || maxWidth < 100) maxWidth = 740;
          if (nw > maxWidth) {
            w = maxWidth;
            h = Math.round(nh * (maxWidth / nw));
          } else {
            w = nw;
            h = nh;
          }
          applySize(w, h);
          const pos = getPos();
          if (pos !== undefined) {
            editor.chain().setNodeSelection(pos).updateAttributes(this.name, { width: w, height: h }).run();
          }
        }
        nodeView.dom.style.visibility = '';
        nodeView.dom.style.pointerEvents = '';
      };
      nodeView.dom.style.visibility = 'hidden';
      nodeView.dom.style.pointerEvents = 'none';

      return {
        dom: figure,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) return false;
          figcaption.textContent = updatedNode.attrs.caption || '';
          figure.style.textAlign = (updatedNode.attrs.align || 'center') === 'left' ? 'left' : (updatedNode.attrs.align || 'center') === 'right' ? 'right' : 'center';
          const newRot = updatedNode.attrs.rotate ?? 0;
          currentRotate = newRot;
          applyRotate(newRot);
          const nw = updatedNode.attrs.width;
          const nh = updatedNode.attrs.height;
          if (nw && nh) applySize(nw, nh);
          return true;
        },
      };
    };
  },
});
